import { SpaComponentPropsResponse } from '@chewy/header-footer-react/dist/common/spa/spa.util';
import { generateMarketPathname } from '@chewy/market-handler';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SEGMENT_TAGS } from '@/analytics/tag-definitions';
import ForterComponent from '@/components/common/ForterComponent/ForterComponent';
import { PageAnimationWrapper } from '@/components/common/PageAnimationWrapper';
import {
  CONFIRMATION_SHEET_QUERY_PARAM,
  MEMBERSHIP_DASHBOARD_LINK,
  SIGN_UP_BASE_URL,
} from '@/components/navigation/constants';
import SignUpPageContent from '@/components/sign-up/SignupPageContent/SignUpPageContent';
import { HttpHeaders } from '@/constants/headers';
import { shouldAccessDashboard } from '@/constants/membership';
import { MemberResponseModel } from '@/generated-client/loyalty-a';
import { PetProfileModel } from '@/generated-client/pet-profile-a';
import { useCmsContent } from '@/hooks/useCmsContent';
import { useDeviceSupportsNativeScrollingExperience } from '@/hooks/useDeviceSupportsNativeScrollingExperience';
import { useFeatureValue } from '@/hooks/useFeatureValue';
import { useMembership } from '@/hooks/useMembership';
import { useSheetActions } from '@/hooks/useSheetActions';
import { useAnalyticsContext } from '@/providers/AnalyticsProvider';
import { SheetProvider } from '@/providers/SheetProvider';
import { fetchGeneralContentPage } from '@/services/content-a/fetch-general-page';
import { getLoyaltyUiAthenaValues } from '@/services/feature-config-service/fetch-feature-toggles';
import { getHeaderFooterComponents } from '@/services/header-footer/header-footer';
import { fetchMembership } from '@/services/loyalty/fetch-membership';
import { fetchActivePetProfiles } from '@/services/pet-profile-a/fetch-pet-profile';
import { getUserContextData } from '@/services/user-context';
import { FeatureConfigKeys } from '@/utils/athena-feature-config';
import {
  getAkamaiHeaders,
  getAuthHeaders,
  isFullyAuthenticated,
  parseTimeZone,
} from '@/utils/auth';
import {
  getBasePath,
  getMarket,
  getSiteId,
  getUSMarketCode,
  isUSMarket,
} from '@/utils/marketHandler';
import { getHeaderUserAgent, isChewyMobileApp } from '@/utils/nma';
import { loginUrl, route, routeToSignupNativeSheet } from '@/utils/router';
import { getLoyaltyPageData, PageType } from '@/utils/ssr';
import { safeTimeZone } from '@/utils/timezone';

import { MarketProps } from '../_app';

export interface HeaderFooterComponentsResponse {
  headerProps: SpaComponentPropsResponse;
  footerProps: SpaComponentPropsResponse;
}

export interface LoyaltyPageProps {
  headerFooter: HeaderFooterComponentsResponse;
  deviceType: string;
}

export interface SignUpProps {
  isAuthenticated: boolean;
  petProfiles: PetProfileModel[];
  marketProps: MarketProps;
  showWebExperience: boolean;
  animationType?: 'fade-in' | 'fade-up';
  timeZone?: string;
}

export default function SignUp({
  petProfiles,
  isAuthenticated,
  showWebExperience,
  animationType,
  marketProps,
  timeZone,
}: SignUpProps) {
  const { t } = useTranslation();
  const signupV2Enabled = useFeatureValue(FeatureConfigKeys.SIGN_UP_PAGE_V2, false);
  const signupNativeSheetsEnabled = useFeatureValue(FeatureConfigKeys.SIGN_UP_NATIVE_SHEETS, false);
  const deviceSupportsNativeExperience = useDeviceSupportsNativeScrollingExperience();

  const showNativeSheetExperience =
    signupNativeSheetsEnabled && !showWebExperience && deviceSupportsNativeExperience;

  const { trackSegmentEvent } = useAnalyticsContext();

  const { showSheet, handleOpenConfirmationSheet, handleCloseSheet } = useSheetActions({
    utilizeQueryParam: true,
    showWebExperience,
  });

  useEffect(() => {
    trackSegmentEvent(
      SEGMENT_TAGS.ViewsChewyPlusLandingPage.eventName,
      SEGMENT_TAGS.ViewsChewyPlusLandingPage.data,
    );
  }, [trackSegmentEvent]);

  const handleAuthCheckAndConfirmationSheet = useCallback(() => {
    if (!isAuthenticated) {
      route(
        loginUrl(
          marketProps?.basePath + '/' + SIGN_UP_BASE_URL + CONFIRMATION_SHEET_QUERY_PARAM,
          marketProps?.market,
        ),
      );
    } else if (showNativeSheetExperience) {
      routeToSignupNativeSheet(marketProps?.market);
    } else {
      handleOpenConfirmationSheet();
    }
  }, [isAuthenticated, showNativeSheetExperience, marketProps, handleOpenConfirmationSheet]);

  const handleHeroButtonClick = useCallback(() => {
    trackSegmentEvent(
      SEGMENT_TAGS.ProceedsToSignupFlyout.eventName,
      SEGMENT_TAGS.ProceedsToSignupFlyout.data,
    );
    handleAuthCheckAndConfirmationSheet();
  }, [handleAuthCheckAndConfirmationSheet, trackSegmentEvent]);

  const handleStickyButtonClick = useCallback(() => {
    trackSegmentEvent(
      SEGMENT_TAGS.ProceedsToSignupFlyoutInfo.eventName,
      SEGMENT_TAGS.ProceedsToSignupFlyoutInfo.data,
    );
    handleAuthCheckAndConfirmationSheet();
  }, [handleAuthCheckAndConfirmationSheet, trackSegmentEvent]);

  return (
    <>
      <Head>
        <title>{t('loyalty.common.meta-title')}</title>
        <meta content={t('loyalty.common.meta-content')} name="description" />
        <meta content="index, follow" name="robots" />
      </Head>
      <SheetProvider
        closeSheet={handleCloseSheet}
        isOpen={showSheet}
        openSheet={handleHeroButtonClick}
      >
        <PageAnimationWrapper
          hasEntryAnimation={animationType != null}
          showWebExperience={showWebExperience}
          type={animationType}
        >
          <SignUpPageContent
            handleAuthCheckAndConfirmationSheet={handleAuthCheckAndConfirmationSheet}
            handleCloseSheet={handleCloseSheet}
            handleHeroButtonClick={handleHeroButtonClick}
            handleStickyButtonClick={handleStickyButtonClick}
            petProfiles={petProfiles}
            showSheet={showSheet}
            showWebExperience={showWebExperience}
            signupV2Enabled={signupV2Enabled}
            timeZone={timeZone}
          />
        </PageAnimationWrapper>
        <ForterComponent />
      </SheetProvider>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const showWebExperience = !isChewyMobileApp(req.headers);
  const userAgent = getHeaderUserAgent(req.headers);
  const authHeaders = getAuthHeaders(req.cookies);
  const akamaiHeaders = getAkamaiHeaders(req);
  const isAuthenticated = isFullyAuthenticated(req.cookies);
  const queryClient = new QueryClient();
  const environment = process.env.ENVIRONMENT;

  const edgescapeHeader = akamaiHeaders?.[HttpHeaders.AkamaiEdgescape] ?? '';
  const timeZone = parseTimeZone(edgescapeHeader) ?? '';
  const formattedTimeZone = safeTimeZone(timeZone);

  const pageData = getLoyaltyPageData(req, PageType.PAGE);

  const athenaValues = await getLoyaltyUiAthenaValues();
  const animationType = athenaValues?.[FeatureConfigKeys.LOYALTY_PAGE_ANIMATION_TYPE];

  const marketProps = {
    isUSMarket: isUSMarket(),
    market: getMarket(),
    basePath: getBasePath(),
    usMarketCode: getUSMarketCode(),
    siteId: getSiteId(),
  };

  const shouldFetchContentV2 = athenaValues?.[FeatureConfigKeys.SIGN_UP_PAGE_V2];

  const [headerFooter, userContextData, petProfiles, cmsContent, membership] = await Promise.all([
    getHeaderFooterComponents(showWebExperience),
    getUserContextData({ ...authHeaders, ...akamaiHeaders }, req?.headers?.cookie),
    fetchActivePetProfiles(authHeaders),
    fetchGeneralContentPage(
      shouldFetchContentV2
        ? { market: marketProps?.market, headers: req.headers, selectionKey: 'sign-up-v2' }
        : { market: marketProps?.market, headers: req.headers },
    ),
    fetchMembership({ market: marketProps?.market, headers: authHeaders }).catch(err => {
      if (isAxiosError(err) && (err?.response?.status === 401 || err?.response?.status === 404)) {
        // Allow these error codes on sign-up page as anybody should be able to access this page
        return {} as MemberResponseModel;
      }
    }),
  ]);

  if (shouldAccessDashboard(membership?.membershipStatus)) {
    return {
      redirect: {
        destination: `${generateMarketPathname(MEMBERSHIP_DASHBOARD_LINK, marketProps?.market)}`,
        permanent: false,
      },
    };
  }

  queryClient.setQueryData([useMembership.key], membership);
  queryClient.setQueryData([useCmsContent.key], cmsContent);

  return {
    props: {
      headerFooter,
      athenaValues,
      isAuthenticated,
      petProfiles,
      showWebExperience,
      environment,
      userAgent,
      pageData,
      dehydratedState: dehydrate(queryClient),
      ...(membership && { membership }),
      ...(userContextData && { userContextData }),
      animationType: animationType ?? null,
      marketProps,
      timeZone: formattedTimeZone,
    },
  };
};
